import {
  Component,
  ChangeDetectionStrategy,
  signal,
  inject,
  OnInit,
  computed
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { Chart, registerables } from 'chart.js';
import {
  CompactType,
  DisplayGrid,
  GridType,
  Gridster,
  GridsterItem,
  type GridsterConfig,
  type GridsterItemConfig
} from 'angular-gridster2';
import { MenuModule } from 'primeng/menu';
import { ButtonModule } from 'primeng/button';
import { v4 as uuidv4 } from 'uuid';
import type { MenuItem } from 'primeng/api';
import { DashboardService, getPeriodRange } from '../../services/dashboard.service';
import type {
  DashboardData,
  DashboardPeriod,
  DashboardPeriodType
} from '../../services/dashboard.service';
import type { DashboardGridItem, DashboardWidgetType } from './dashboard-widget-types';
import { DASHBOARD_LAYOUT_STORAGE_KEY } from './dashboard-widget-types';
import {
  DASHBOARD_WIDGET_REGISTRY,
  DASHBOARD_WIDGET_TYPE_ORDER,
  defaultDashboardLayout
} from './dashboard-widgets.registry';
import { sanitizeDashboardGridItems } from './dashboard-grid.utils';
import { DashKpiCollectionWidgetComponent } from './widgets/dash-kpi-collection.widget';
import { DashKpiProjectionWidgetComponent } from './widgets/dash-kpi-projection.widget';
import { DashKpiPendingWidgetComponent } from './widgets/dash-kpi-pending.widget';
import { DashKpiRateWidgetComponent } from './widgets/dash-kpi-rate.widget';
import { DashSalesChartWidgetComponent } from './widgets/dash-sales-chart.widget';
import { DashCollectionTimelineWidgetComponent } from './widgets/dash-collection-timeline.widget';
import { DashCollectionBreakdownWidgetComponent } from './widgets/dash-collection-breakdown.widget';
import { DashUpcomingOverdueWidgetComponent } from './widgets/dash-upcoming-overdue.widget';
import { DashInvoiceStatusWidgetComponent } from './widgets/dash-invoice-status.widget';
import { DashRecentPaymentsWidgetComponent } from './widgets/dash-recent-payments.widget';

/** px por fila en modo ScrollVertical (estimado para min-height del contenedor) */
const DASH_ROW_PX_ESTIMATE = 90;
const DASH_MARGIN = 8;

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    Gridster,
    GridsterItem,
    MenuModule,
    ButtonModule,
    DashKpiCollectionWidgetComponent,
    DashKpiProjectionWidgetComponent,
    DashKpiPendingWidgetComponent,
    DashKpiRateWidgetComponent,
    DashSalesChartWidgetComponent,
    DashCollectionTimelineWidgetComponent,
    DashCollectionBreakdownWidgetComponent,
    DashUpcomingOverdueWidgetComponent,
    DashInvoiceStatusWidgetComponent,
    DashRecentPaymentsWidgetComponent
  ],
  styles: [
    `
      :host { display: block; width: 100%; }

      :host ::ng-deep gridster {
        background: rgb(248 250 252) !important;
      }
      :host ::ng-deep gridster-item {
        overflow: hidden;
      }
      :host ::ng-deep .pento-dash-widget-card {
        position: absolute;
        inset: 0;
        display: flex;
        flex-direction: column;
        box-sizing: border-box;
        border-radius: 0.5rem;
        background: #fff;
        border: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
        overflow: hidden;
      }
      /* El elemento host de cada widget (selector app-dash-*) debe ser display:block height:100% */
      :host ::ng-deep .pento-dash-no-drag > * {
        display: block;
        height: 100%;
      }
    `
  ]
})
export class DashboardComponent implements OnInit {
  private dashboardService = inject(DashboardService);

  constructor() {
    Chart.register(...registerables);
  }

  data = signal<DashboardData | null>(null);
  period = signal<DashboardPeriod>({ type: 'month' });
  customDateFrom = signal<string>('');
  customDateTo = signal<string>('');
  gridItems = signal<DashboardGridItem[]>(this.readStoredLayout());

  readonly periodOptions: { value: DashboardPeriodType; label: string }[] = [
    { value: 'today', label: 'Hoy' },
    { value: 'week', label: 'Semana' },
    { value: 'month', label: 'Mes' },
    { value: 'year', label: 'Año' },
    { value: 'custom', label: 'Personalizado' }
  ];

  readonly addWidgetMenuItems = computed<MenuItem[]>(() =>
    DASHBOARD_WIDGET_TYPE_ORDER.map((type) => ({
      label: DASHBOARD_WIDGET_REGISTRY[type].title,
      command: () => this.addWidget(type)
    }))
  );

  /**
   * IMPORTANTE: objeto plano, NO signal().
   * Si se pasa signal() como [options]="gridsterOptions", Angular entrega el objeto Signal
   * (no su valor) a gridster, que entonces usa todos sus defaults (Fit mode, rows=1 → 30px).
   */
  readonly gridsterOptions: GridsterConfig = {
    gridType: GridType.ScrollVertical,
    displayGrid: DisplayGrid.Always,
    // setGridSize: true → gridster calcula y aplica su propio height inline (no depende del padre)
    setGridSize: true,
    rowHeightRatio: 1,
    useBodyForBreakpoint: true,
    mobileBreakpoint: 480,
    minCols: 12,
    maxCols: 12,
    margin: DASH_MARGIN,
    outerMargin: true,
    pushItems: true,
    defaultItemCols: 3,
    defaultItemRows: 4,
    draggable: {
      enabled: true,
      dragHandleClass: 'pento-dash-drag-handle',
      ignoreContent: true,
      ignoreContentClass: 'pento-dash-no-drag'
    },
    resizable: { enabled: true },
    compactType: CompactType.None,
    initCallback: (_grid, api) => {
      queueMicrotask(() => api.resize());
      setTimeout(() => api.resize(), 250);
    },
    itemChangeCallback: (item: GridsterItemConfig) => this.syncItemFromGridster(item),
    itemResizeCallback: (item: GridsterItemConfig) => this.syncItemFromGridster(item)
  };

  /**
   * Altura mínima estimada del contenedor del grid.
   * Con ScrollVertical + setGridSize, gridster pone su propio height.
   * Este valor es un fallback CSS para que el contenedor no colapse antes de que cargue JS.
   */
  readonly gridsterMinHeightPx = computed(() => {
    const items = this.gridItems();
    if (!items.length) return 500;
    const maxRow = items.reduce(
      (m, i) => Math.max(m, (Number(i.y) || 0) + (Number(i.rows) || 4)),
      0
    );
    return maxRow * (DASH_ROW_PX_ESTIMATE + DASH_MARGIN) + DASH_MARGIN * 3;
  });

  ngOnInit(): void {
    this.load();
  }

  setPeriod(type: DashboardPeriodType): void {
    if (type === 'custom') {
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, '0');
      const first = `${y}-${m}-01`;
      const last = new Date(y, now.getMonth() + 1, 0);
      const lastStr = `${y}-${m}-${String(last.getDate()).padStart(2, '0')}`;
      this.customDateFrom.set(first);
      this.customDateTo.set(lastStr);
      this.period.set({ type: 'custom' });
      return;
    }
    this.period.set({ type });
    this.load();
  }

  applyCustomPeriod(): void {
    const from = this.customDateFrom();
    const to = this.customDateTo();
    if (!from || !to) return;
    this.period.set({ type: 'custom', dateFrom: from, dateTo: to });
    this.load();
  }

  load(): void {
    this.data.update((d) => {
      const base: DashboardData = d ?? {
        collection: { amount: 0, count: 0 },
        projection: { amount: 0, count: 0 },
        upcomingCharges: [],
        overdueCharges: [],
        recentPayments: [],
        salesByDay: [],
        heatMapData: [],
        rates: {
          collectionRate: 0,
          overdueRate: 0,
          totalIssued: 0,
          totalPaid: 0,
          totalPending: 0
        },
        invoicesByStatus: {},
        loading: false
      };
      return { ...base, loading: true, error: undefined };
    });
    this.dashboardService.loadDashboard(this.period()).subscribe((d) => this.data.set(d));
  }

  refresh(): void {
    this.load();
  }

  periodLabel(): string {
    const p = this.period();
    const { from, to } = getPeriodRange(p);
    return `${from.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })} – ${to.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  }

  todayLabel(): string {
    return new Date().toLocaleDateString('es-MX', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  }

  widgetTitle(type: DashboardWidgetType): string {
    return DASHBOARD_WIDGET_REGISTRY[type].title;
  }

  addWidget(type: DashboardWidgetType): void {
    const meta = DASHBOARD_WIDGET_REGISTRY[type];
    const items = this.gridItems();
    const maxY = items.reduce((m, i) => Math.max(m, (Number(i.y) || 0) + (Number(i.rows) || 4)), 0);
    const newItem: DashboardGridItem = {
      id: uuidv4(),
      widgetType: type,
      cols: meta.defaultCols,
      rows: meta.defaultRows,
      x: 0,
      y: maxY,
      minItemCols: meta.minCols,
      minItemRows: meta.minRows
    };
    const next = [...items, newItem];
    this.gridItems.set(next);
    this.persistLayout(next);
  }

  removeWidget(id: string): void {
    const next = this.gridItems().filter((i) => i.id !== id);
    this.gridItems.set(next);
    this.persistLayout(next);
  }

  resetLayout(): void {
    const next = sanitizeDashboardGridItems(defaultDashboardLayout());
    this.gridItems.set(next);
    this.persistLayout(next);
  }

  private syncItemFromGridster(item: GridsterItemConfig): void {
    const g = item as DashboardGridItem;
    if (!g?.id) return;
    const next = this.gridItems().map((i) =>
      i.id === g.id
        ? ({
            ...i,
            x: item.x,
            y: item.y,
            cols: Number(item.cols) || i.cols,
            rows: Number(item.rows) || i.rows
          } as DashboardGridItem)
        : i
    );
    this.gridItems.set(next);
    this.persistLayout(next);
  }

  private readStoredLayout(): DashboardGridItem[] {
    const fallback = () => sanitizeDashboardGridItems(defaultDashboardLayout());
    if (typeof localStorage === 'undefined') return fallback();
    try {
      const raw = localStorage.getItem(DASHBOARD_LAYOUT_STORAGE_KEY);
      if (!raw) return fallback();
      const sanitized = sanitizeDashboardGridItems(JSON.parse(raw) as unknown);
      return sanitized.length > 0 ? sanitized : fallback();
    } catch {
      return fallback();
    }
  }

  private persistLayout(items: DashboardGridItem[]): void {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(
        DASHBOARD_LAYOUT_STORAGE_KEY,
        JSON.stringify(sanitizeDashboardGridItems(items))
      );
    } catch {
      /* quota */
    }
  }
}
